import { TableFooter } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { OLPaper } from "OLPaper";
import { useAppSelector } from "app/hooks";
import { Selectable } from "components/Table/useTableSelectable";
import { useMessage } from "hooks/intl";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { selectTables } from "reduxState/tables/tablesSlice";
import { Order } from "types/listing";
import { alphabetizeByProperty } from "utilities/arrays";
import { ColumnManager } from "./ColumnManager/ColumnManager";
import { NoFilterResults } from "./NoFilterResults";
import { OLTableBody } from "./OLTableBody";
import { OLTableHead } from "./OLTableHead";
import { useStyles } from "./styles";
import { getColumnsOrdered } from "./tableHelpers";

export interface OrderColumn {
  order: Order;
  property: string;
  rowsPerPage: number;
  page: number;
}

interface OLTablePagination {
  count: number;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (rows: number) => void;
  page: number;
  rowsPerPage: number;
}

export interface OLTableActions {
  action?: (
    id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
    item?: Row,
  ) => void;
  disableCondition?: (item: Row) => boolean;
  hideCondition?: (item: Row) => boolean;
  label: string;
}

export interface Column {
  align?: "left" | "right" | "center" | "inherit";
  customWidth?: string;
  label: string;
  property: string;
  tooltip?: string | React.ReactElement;
  hasMinWidthThreshold?: boolean;
  testId?: string;
  isSortable: boolean;
  isVisible?: boolean;
  isSticky: boolean;
}

export enum CellAlign {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
  INHERIT = "inherit",
}

export interface Cell {
  align?: "left" | "right" | "center" | "inherit";
  content?: React.ReactNode;
  customOverflowTooltip?: React.ReactNode;
  emptyString?: string;
  tooltip?: React.ReactElement;
  nonClickable?: boolean;
  property?: string;
  onDirectClick?: () => void;
  maxWidth?: string;
  dataHjSuppress?: boolean;
  disableOverflowTooltip?: boolean;
}

export interface Row {
  cells: Cell[];
  id: string;
  metadata?: any;
  data?: any;
}

export function createHeadItems(items: any): Column[] {
  return items.map(
    (item: {
      align?: string;
      headerText: string;
      property: string;
      tooltip?: string | React.ReactElement;
      customWidth?: string;
      hasMinWidthThreshold?: boolean;
      isSortable: boolean;
      isVisible?: boolean;
    }) => ({
      align: item.align,
      label: item.headerText,
      property: item.property,
      tooltip: item.tooltip,
      customWidth: item.customWidth,
      hasMinWidthThreshold: item.hasMinWidthThreshold,
      isSortable: item.isSortable,
      isVisible: item.isVisible,
    }),
  );
}

interface OLTableProps {
  onRowClick?: (id: string) => void;
  actions?: {
    body: Array<OLTableActions>;
    head?: Array<OLTableActions>;
    headAction?: () => void;
  };
  className?: string;
  columns?: Column[];
  noDataText?: string;
  onSort: (order: OrderColumn) => void;
  pagination?: OLTablePagination;
  rows?: Array<Row>;
  selectable?: Selectable;
  sort?: OrderColumn;
  "data-test-id"?: string;
  tableName: string;
  setColumnOrdering?: (columns: Column[], tableName: string) => void;
  setTablePaging?: (rowsPerPage: number, tableName: string) => void;
  disableColumnManager?: boolean;
  fullView?: boolean;
  disableActions?: boolean;
  clearFilters?: () => void;
  customActionsOrder?: boolean;
}

const TableComponent = ({
  onRowClick,
  actions,
  className,
  columns,
  noDataText,
  onSort,
  pagination,
  rows,
  selectable,
  sort = {
    order: "asc",
    property: "",
    rowsPerPage: 10,
    page: 1,
  },
  "data-test-id": dataTestId,
  tableName,
  setColumnOrdering,
  disableColumnManager,
  fullView,
  disableActions,
  clearFilters,
  customActionsOrder = false,
}: OLTableProps) => {
  const [columnsOrdered, setColumns] = useState<Column[]>([]);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const columnRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const classes = useStyles({ fullView: fullView });
  const { message } = useMessage();
  const refTableContainer = useRef(null);
  const refTable = useRef(null);
  const currentTable = useAppSelector(selectTables)[tableName];
  const isAnyFilterApplied = currentTable?.filters
    ? currentTable?.filters.length > 0
    : false;

  useEffect(() => {
    const widths = columnRefs.current.map((cell) => cell?.offsetWidth || 0);
    setColumnWidths(widths);
  }, [columns]);

  useEffect(() => {
    if (columns) {
      if (disableColumnManager) {
        return setColumns(columns);
      }
      const appliedOrdering = currentTable?.column_ordering;

      if (appliedOrdering && appliedOrdering.length > 0) {
        const columnsOrdered = getColumnsOrdered({
          appliedOrdering: appliedOrdering,
          columns: columns,
        });
        return setColumns(columnsOrdered as Column[]);
      }

      const mapedColumns = columns.map((col, idx) => {
        const isVisible = typeof col.isVisible === "boolean";

        return {
          ...col,
          isVisible: isVisible ? col.isVisible : true,
          isSticky: idx === 0, // Initial set first column as sticky
        };
      });

      setColumns(mapedColumns as Column[]);
    }
    // eslint-disable-next-line
  }, [columns]);

  const orderedRows = useMemo(
    () =>
      rows?.map((row) => {
        const columns = disableColumnManager
          ? columnsOrdered
          : [...columnsOrdered].filter((col) => col.isVisible);
        const orderderCells: Cell[] = [];

        columns.forEach((column, index) => {
          const matchedCell = row.cells.find(
            (cell) => cell.property === column.property,
          );

          orderderCells.push(matchedCell || row.cells[index]);
        });

        return {
          ...row,
          cells: orderderCells,
        };
      }),
    // eslint-disable-next-line
    [columnsOrdered, rows],
  );
  const visibleColumns = useMemo(
    () =>
      disableColumnManager
        ? columnsOrdered
        : [...columnsOrdered].filter((column) => column.isVisible),
    [columnsOrdered, disableColumnManager],
  );

  const isPaginationAdded = !!pagination;

  const saveColumnsOrdering = (columns: Column[]) => {
    if (setColumnOrdering) {
      setColumnOrdering(columns, tableName);
    }
    setColumns(columns);
  };

  const paginationProps = disableColumnManager
    ? { component: "div" }
    : {
        component: ColumnManager,
        setColumns: saveColumnsOrdering,
        columns: columnsOrdered,
        fullView: fullView,
      };
  const areAllRowsSelected =
    selectable &&
    orderedRows?.every((row) => {
      return !!selectable.rowsSelected.find(
        (selectedRow) => selectedRow.id === row.id,
      );
    });

  const showCleanFiltersView =
    isAnyFilterApplied && orderedRows && orderedRows.length === 0;

  const calculateColumnLeftOffset = (columnIndex: number) => {
    let left = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (visibleColumns[i].isSticky) {
        left += columnWidths[i] || 0;
      }
    }
    return left;
  };

  return showCleanFiltersView && clearFilters ? (
    <NoFilterResults clearFilters={clearFilters} />
  ) : (
    <OLPaper noPadding className={`${fullView ? classes.fullView : ""}`}>
      <TableContainer
        data-test-id="table-container"
        className={`${classes.root} ${className}`}
        ref={refTableContainer}
      >
        {orderedRows ? (
          <Table
            data-test-id={dataTestId && `${dataTestId}-table`}
            className={classes.Table}
            ref={refTable}
            stickyHeader={fullView}
          >
            {columns && (
              <OLTableHead
                actions={alphabetizeByProperty(actions?.head, "label")}
                classes={classes}
                columns={visibleColumns}
                onSort={onSort}
                selectable={
                  selectable && {
                    disabled: selectable.disabled,
                    allSelected: !!areAllRowsSelected,
                    unselectAll: () =>
                      selectable.unselectAll(orderedRows.map((row) => row.id)),
                    selectAll: () =>
                      selectable.selectAll(
                        orderedRows.map((row) => ({
                          id: row.id,
                          data: row.data,
                        })),
                      ),
                  }
                }
                sort={sort}
                data-test-id={dataTestId}
                disableActions={disableActions}
                columnRefs={columnRefs}
                calculateColumnLeftOffset={calculateColumnLeftOffset}
              />
            )}
            {orderedRows.length ? (
              <OLTableBody
                columns={visibleColumns}
                actions={
                  customActionsOrder
                    ? actions?.body
                    : alphabetizeByProperty(actions?.body, "label")
                }
                items={orderedRows}
                onRowClick={onRowClick}
                selectable={
                  selectable && {
                    selectRow: selectable.selectRow,
                    unselectRow: selectable.unselectRow,
                    rowsSelected: selectable.rowsSelected,
                    disabled: selectable.disabled,
                  }
                }
                data-test-id={dataTestId}
                disableActions={disableActions}
                columnRefs={columnRefs}
                calculateColumnLeftOffset={calculateColumnLeftOffset}
              />
            ) : (
              <caption className={classes.caption}>
                {noDataText || message({ id: "noData" })}
              </caption>
            )}
            {!isPaginationAdded && !disableColumnManager && (
              <TableFooter>
                <ColumnManager
                  columns={columnsOrdered}
                  setColumns={saveColumnsOrdering}
                  fullView={fullView}
                />
              </TableFooter>
            )}
          </Table>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {[...Array(5)].map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton height={20} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(pagination ? pagination.rowsPerPage : 5)].map(
                (_, index) => (
                  <TableRow key={index} style={{ height: 49 }}>
                    {[...Array(5)].map((_, index) => (
                      <TableCell
                        className={classes.skeletonTableCell}
                        key={index}
                      >
                        <Skeleton height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {pagination?.count ? (
        <TablePagination
          className={classes.pagination}
          data-test-id="table-pagination"
          count={pagination.count}
          labelRowsPerPage={message({ id: "rowsPerPage" })}
          onPageChange={(_, page) => {
            onSort({ ...sort, page: page + 1 });
            pagination.onChangePage(page + 1);
          }}
          onRowsPerPageChange={(e) => {
            onSort({
              ...sort,
              page: 1,
              rowsPerPage: parseInt(e.target.value),
            });
            pagination.onChangeRowsPerPage(parseInt(e.target.value));
          }}
          page={pagination.page - 1}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          {...paginationProps}
        />
      ) : null}
    </OLPaper>
  );
};
export const OLTable = TableComponent;
